import { createRng } from './rng.js';
import { getArchetype } from './archetypes/index.js';
import { resolveSynergy } from './synergy.js';
export const SIM_VERSION = '0.1.0';
export const HARD_CAP_TICKS = 90;
function initRuntime(stats) {
    return {
        hp: stats.hp, hpMax: stats.hp,
        atk: stats.atk, def: stats.def, spd: stats.spd, crit: stats.crit,
        ultCharge: 0, cooldowns: {}, statuses: [], reviveAvailable: false, alive: true
    };
}
function applyPassivesAtInit(state, side, rng) {
    const lineup = side === 'A' ? state.lineupA : state.lineupB;
    for (let slot = 0; slot < 5; slot++) {
        const lineupSlot = lineup.slots[slot];
        const passiveId = lineupSlot.loadout.passiveId;
        if (!passiveId)
            continue;
        const skill = lineupSlot.skills.find(s => s.id === passiveId);
        if (!skill)
            continue;
        const arch = getArchetype(skill.archetypeId);
        arch.apply({ state, source: { side, slot }, params: skill.params, rng });
    }
}
function applySynergyBuffs(state, side) {
    const lineup = side === 'A' ? state.lineupA : state.lineupB;
    const triggers = resolveSynergy(lineup);
    for (const t of triggers) {
        const mult = t.doubled ? 2 : 1;
        const sideArr = side === 'A' ? state.sideA : state.sideB;
        for (const r of sideArr) {
            r.statuses.push({
                id: `synergy_${t.groupId}`, kind: 'buff',
                remainingTicks: HARD_CAP_TICKS,
                params: { synergyAtkPct: 0.1 * mult }
            });
        }
    }
}
function applyDots(state, side) {
    const sideArr = side === 'A' ? state.sideA : state.sideB;
    for (let slot = 0; slot < sideArr.length; slot++) {
        const r = sideArr[slot];
        if (!r.alive)
            continue;
        for (const s of r.statuses) {
            if (s.kind === 'dot' && s.remainingTicks > 0) {
                const dmg = (s.params.dmgPerTick ?? 0);
                if (dmg <= 0)
                    continue;
                const dealt = Math.min(dmg, r.hp);
                r.hp -= dealt;
                state.events.push({ t: state.tick, kind: 'damage', side, slot, amount: dealt, isCrit: false });
                if (r.hp <= 0 && r.alive) {
                    r.hp = 0;
                    r.alive = false;
                    state.events.push({ t: state.tick, kind: 'ko', side, slot });
                }
            }
        }
    }
}
function tickStatuses(state, side) {
    const sideArr = side === 'A' ? state.sideA : state.sideB;
    for (let slot = 0; slot < sideArr.length; slot++) {
        const r = sideArr[slot];
        r.statuses = r.statuses.filter(s => {
            // Permanent passives never expire
            if (s.remainingTicks === Number.MAX_SAFE_INTEGER)
                return true;
            s.remainingTicks -= 1;
            if (s.remainingTicks <= 0) {
                state.events.push({ t: state.tick, kind: 'status_expire', side, slot, status: s.kind });
                return false;
            }
            return true;
        });
    }
}
function tickCooldowns(state, side) {
    const sideArr = side === 'A' ? state.sideA : state.sideB;
    for (const r of sideArr) {
        for (const k of Object.keys(r.cooldowns)) {
            r.cooldowns[k] = Math.max(0, (r.cooldowns[k] ?? 0) - 1);
        }
    }
}
function fireReadySkills(state, side, rng) {
    const lineup = side === 'A' ? state.lineupA : state.lineupB;
    const sideArr = side === 'A' ? state.sideA : state.sideB;
    for (let slot = 0; slot < 5; slot++) {
        const r = sideArr[slot];
        if (!r.alive)
            continue;
        if (r.statuses.some(s => s.kind === 'stun'))
            continue;
        const lineupSlot = lineup.slots[slot];
        // ultimate first when fully charged
        if (r.ultCharge >= 100 && lineupSlot.loadout.ultimateId) {
            const ultSkill = lineupSlot.skills.find(s => s.id === lineupSlot.loadout.ultimateId);
            if (ultSkill) {
                state.events.push({
                    t: state.tick, kind: 'skill_fire', side, sourceSlot: slot,
                    skillId: ultSkill.id, targets: []
                });
                getArchetype(ultSkill.archetypeId).apply({
                    state, source: { side, slot }, params: ultSkill.params, rng
                });
                r.ultCharge = 0;
                continue;
            }
        }
        // active skills (silence blocks actives, but not ult)
        if (r.statuses.some(s => s.kind === 'silence'))
            continue;
        for (const aId of lineupSlot.loadout.activeIds) {
            if (!aId)
                continue;
            const skill = lineupSlot.skills.find(s => s.id === aId);
            if (!skill)
                continue;
            const cd = r.cooldowns[skill.id] ?? 0;
            if (cd > 0)
                continue;
            state.events.push({
                t: state.tick, kind: 'skill_fire', side, sourceSlot: slot,
                skillId: skill.id, targets: []
            });
            getArchetype(skill.archetypeId).apply({
                state, source: { side, slot }, params: skill.params, rng
            });
            r.cooldowns[skill.id] = skill.cooldown;
            r.ultCharge = Math.min(100, r.ultCharge + 8);
            break; // one active per tick per slot
        }
    }
}
function bothAlive(state) {
    return state.sideA.some(r => r.alive) && state.sideB.some(r => r.alive);
}
function totalHpPct(arr) {
    const total = arr.reduce((s, r) => s + r.hpMax, 0);
    const cur = arr.reduce((s, r) => s + r.hp, 0);
    return total === 0 ? 0 : cur / total;
}
export function runMatch(seed, A, B) {
    const rng = createRng(seed);
    const state = {
        tick: 0,
        sideA: A.slots.map(s => initRuntime(s.member.stats)),
        sideB: B.slots.map(s => initRuntime(s.member.stats)),
        events: [],
        lineupA: JSON.parse(JSON.stringify(A)),
        lineupB: JSON.parse(JSON.stringify(B))
    };
    applyPassivesAtInit(state, 'A', rng);
    applyPassivesAtInit(state, 'B', rng);
    applySynergyBuffs(state, 'A');
    applySynergyBuffs(state, 'B');
    while (state.tick < HARD_CAP_TICKS && bothAlive(state)) {
        state.tick += 1;
        tickCooldowns(state, 'A');
        tickCooldowns(state, 'B');
        fireReadySkills(state, 'A', rng);
        fireReadySkills(state, 'B', rng);
        applyDots(state, 'A');
        applyDots(state, 'B');
        if (!bothAlive(state))
            break;
        tickStatuses(state, 'A');
        tickStatuses(state, 'B');
        for (const r of state.sideA)
            if (r.alive)
                r.ultCharge = Math.min(100, r.ultCharge + 1);
        for (const r of state.sideB)
            if (r.alive)
                r.ultCharge = Math.min(100, r.ultCharge + 1);
    }
    const aAlive = state.sideA.some(r => r.alive);
    const bAlive = state.sideB.some(r => r.alive);
    let winner;
    if (aAlive && !bAlive)
        winner = 'A';
    else if (bAlive && !aAlive)
        winner = 'B';
    else if (aAlive && bAlive) {
        const aPct = totalHpPct(state.sideA);
        const bPct = totalHpPct(state.sideB);
        if (aPct > bPct)
            winner = 'A';
        else if (bPct > aPct)
            winner = 'B';
        else
            winner = 'draw';
    }
    else {
        winner = 'draw';
    }
    return {
        winner,
        durationTicks: state.tick,
        finalHpA: state.sideA.map(r => r.hp),
        finalHpB: state.sideB.map(r => r.hp),
        events: state.events,
        seed,
        simVersion: SIM_VERSION
    };
}
